import { useEffect, useState } from "react";
import { Copy, LinkSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

export const ShareDialog = ({ clientId, clientCode }) => {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [emailTo, setEmailTo] = useState("");

  const load = () => api.get(`/clients/${clientId}/share-links`).then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/share-links`);
      setLinks((ls) => [...ls, data]);
      toast.success("Share link created");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const emailNew = async () => {
    if (!/.+@.+\..+/.test(emailTo)) { toast.error("Enter a valid email address"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/share-links`);
      await api.post(`/clients/${clientId}/share-links/${data.token}/email`, { email: emailTo });
      setLinks((ls) => [...ls, data]);
      toast.success(`Signing link emailed to ${emailTo}`);
      setEmailTo("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (token) => {
    try {
      await api.post(`/clients/${clientId}/share-links/${token}/revoke`);
      setLinks((ls) => ls.filter((l) => l.token !== token));
      toast.success("Link revoked");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/share/${token}`);
    toast.success("Link copied");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="share-dialog-button">
          <LinkSimple size={16} className="mr-1.5" /> Share for Signatures
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>Share documents for signature</DialogTitle>
          <DialogDescription>
            Send this link to the client or family. They can review and sign all packet documents and the care plan approval — no account needed. Links expire after 7 days and can be revoked anytime.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {links.map((l) => (
            <div key={l.token} className="flex items-center gap-2 rounded-lg border border-stone-200 bg-accent/60 px-3 py-2" data-testid={`share-link-${l.token.slice(0, 8)}`}>
              <span className="min-w-0 flex-1 truncate text-xs text-stone-600">{window.location.origin}/share/{l.token}</span>
              <span className="shrink-0 text-xs text-stone-400">expires {String(l.expires_at).slice(0, 10)}</span>
              <button onClick={() => window.open(`${window.location.origin}/share/${l.token}`, "_blank")} className="shrink-0 rounded border border-primary/40 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary hover:text-white" data-testid={`share-open-${l.token.slice(0, 8)}`}>Open here</button>
              <button onClick={() => copy(l.token)} className="shrink-0 text-primary hover:text-stone-900" data-testid={`share-copy-${l.token.slice(0, 8)}`}><Copy size={15} /></button>
              <button onClick={() => revoke(l.token)} className="shrink-0 text-stone-400 hover:text-[#C0392B]" data-testid={`share-revoke-${l.token.slice(0, 8)}`}><X size={15} /></button>
            </div>
          ))}
          {links.length === 0 && <p className="text-sm text-stone-500">No active links yet.</p>}
          <Button onClick={create} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="create-share-link-button">
            {busy ? "Creating..." : "Create New Link"}
          </Button>
          <div className="flex gap-2 border-t border-stone-100 pt-3">
            <input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
              placeholder="client-or-family@email.com" data-testid="share-email-input"
              className="h-9 flex-1 rounded-md border border-stone-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
            <Button onClick={emailNew} disabled={busy} variant="outline" className="border-primary/40 text-primary" data-testid="share-email-send">
              Email signing link
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
